/**
 * PrismQuery i18n dictionary.
 * Supported languages: English (en), Chinese Simplified (zh), Hindi (hi),
 * Spanish (es), Arabic (ar — RTL).
 *
 * Every UI-facing string in the app should live here so that switching
 * languages via the header selector re-renders the entire product.
 *
 * The `format(key, vars)` helper supports {name}-style placeholders,
 * so translations can keep the same natural word order.
 */

export const LANGUAGES = [
  { code: "en", label: "English", native: "English",   dir: "ltr" },
  { code: "zh", label: "Chinese", native: "简体中文",     dir: "ltr" },
  { code: "hi", label: "Hindi",   native: "हिन्दी",       dir: "ltr" },
  { code: "es", label: "Spanish", native: "Español",   dir: "ltr" },
  { code: "ar", label: "Arabic",  native: "العربية",   dir: "rtl" },
];

export const LANGUAGE_CODES = LANGUAGES.map((l) => l.code);
export const DEFAULT_LANGUAGE = "en";

export const isRTL = (lang) =>
  (LANGUAGES.find((l) => l.code === lang)?.dir || "ltr") === "rtl";

/* ------------------------------------------------------------------ */
/* Dictionaries — keep keys stable; edit values only.                  */
/* ------------------------------------------------------------------ */
const DICT = {
  en: {
    "brand.tagline": "5-model split view",
    "nav.coins": "coins",
    "nav.buyCoins": "Buy more coins",
    "nav.profile": "Profile & settings",
    "nav.about": "About wangram.com",
    "nav.buy": "Buy coins",
    "nav.signOut": "Sign out",
    "nav.language": "Language",
    "nav.newSession": "Start a new session",

    "hero.eyebrow": "no subscription · pay-as-you-go",
    "hero.title1": "Pro AI models,",
    "hero.title2": "without the monthly bill.",
    "hero.subtitle": "Ask a question and get answers from OpenAI, Anthropic, Google, xAI, and DeepSeek — side by side. One coin per query. Buy 2 for $0.99, nothing expires, no subscription.",
    "hero.footnote": "You only pay when you actually use it.",

    "search.askAnything": "Ask anything…",
    "search.askAnother": "Ask another question…",
    "search.askAboutImage": "What do you want to know about this image?",
    "search.compare": "Compare",
    "search.running": "Running",
    "search.dropHere": "drop images here",
    "search.outOfCoins": "Out of coins — buy a pack to continue",
    "search.outOfCoinsShort": "Out of coins",
    "search.attachHint": "drop, paste, or attach an image · voice input supported",
    "search.newInline": "New",
    "search.yourQuery": "your query",
    "search.clearInput": "Clear input",

    "balance.low": "low balance",
    "balance.out": "out of coins",
    "balance.left": "You have {credits} coin{plural} left. 1 coin per comparison.",

    "history.session": "Session",
    "history.title": "History",
    "history.new": "New",
    "history.empty": "No queries yet",
    "history.delete": "Delete",

    "panel.awaiting": "awaiting query",
    "panel.tracing": "tracing beam",
    "panel.copy": "Copy",
    "panel.copied": "Copied",
    "panel.error": "error",
    "panel.unknownError": "Unknown error",
    "panel.copyAria": "Copy response",

    "leaderboard.title": "your win rates",
    "leaderboard.pins": "{count} pin{plural}",

    "toast.compared": "Compared {ok}/{total} models",
    "toast.refunded": "Only {ok}/{total} models answered — 1 coin refunded.",
    "toast.insufficient": "Insufficient coins.",
    "toast.buyCoins": "Buy coins",
    "toast.searchFailed": "Search failed. Please try again.",
    "toast.historyLoadFail": "Could not load history item",
    "toast.deleted": "Deleted",
    "toast.deleteFail": "Delete failed",
    "toast.saveFirst": "Save the search first — refresh in a moment.",
    "toast.pinned": "Pinned as best answer.",
    "toast.pinFail": "Could not pin.",

    "login.eyebrow": "login",
    "login.title": "Access Terminal",
    "login.google": "Continue with Google",
    "login.or": "or",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Enter",
    "login.noAccount": "No account?",
    "login.createOne": "Create one",
    "login.shellNote": "No subscription — pay only when you use it.",
    "login.registeredOnly": "Registered access only",
    "login.forgot": "Forgot your password?",

    "forgot.eyebrow": "password reset",
    "forgot.title": "Reset password",
    "forgot.body": "Enter the email you signed up with. We'll send a link to reset your password. It's valid for 1 hour.",
    "forgot.email": "Email",
    "forgot.submit": "Send reset link",
    "forgot.remembered": "Remembered it? Sign in",
    "forgot.error": "Something went wrong. Please try again.",
    "forgot.checkInbox": "Check your inbox",
    "forgot.sentBody": "If an account exists for {email}, we've sent a reset link. Check your spam folder too — it's valid for 1 hour.",
    "forgot.backToLogin": "Back to sign in",

    "reset.eyebrow": "new password",
    "reset.title": "Choose a new password",
    "reset.body": "Pick something at least 6 characters long. You'll be signed out of any old sessions.",
    "reset.newPw": "New password",
    "reset.confirmPw": "Confirm new password",
    "reset.submit": "Update password",
    "reset.mismatch": "The two passwords don't match.",
    "reset.success": "Password updated",
    "reset.redirecting": "Redirecting you to sign in…",
    "reset.error": "Could not reset password. The link may have expired.",
    "reset.missingToken": "This link is missing its token. Request a new one below.",
    "reset.requestNew": "Request a new link",

    "sessions.eyebrow": "conversations",
    "sessions.title": "Sessions",
    "sessions.empty": "No sessions yet",
    "sessions.rename": "Rename",
    "sessions.delete": "Delete session",
    "sessions.renamed": "Session renamed",
    "sessions.renameFail": "Could not rename session.",
    "sessions.deleteConfirm": "Delete this session and every search inside it? This can't be undone.",
    "sessions.deleted": "Session deleted",
    "sessions.deleteFail": "Could not delete session.",
    "sessions.saveRename": "Save",
    "sessions.cancelRename": "Cancel",
    "sessions.moreActions": "More actions",
    "sessions.expand": "Expand sidebar",
    "sessions.collapse": "Collapse sidebar",
    "sessions.turnCount": "{count} turn{plural} in this session",

    "auth.modelsHeader": "You'll compare answers from these 5 frontier models",
    "auth.modelsFooter": "Updated live · one coin per 5-model compare",

    "register.eyebrow": "register",
    "register.title": "Create Access",
    "register.google": "Sign up with Google",
    "register.bonusStrong": "2 free coins",
    "register.bonusRest": " on signup — worth 2 five-model comparisons.",
    "register.name": "Display Name",
    "register.optional": "optional",
    "register.email": "Email",
    "register.password": "Password (min 6)",
    "register.confirmPassword": "Confirm password",
    "register.pwMismatch": "The two passwords don't match.",
    "register.submit": "Create Account",
    "register.already": "Already registered?",
    "register.signIn": "Sign in",
    "register.pwShort": "Password must be at least 6 characters.",
    "register.agreePrefix": "By creating an account, I agree to the",
    "register.agreeAnd": "and",
    "register.mustAgree": "Please accept the Terms of Service and Privacy Policy to continue.",
    "register.needBotCheck": "Please complete the bot check to continue.",

    "profile.eyebrow": "profile",
    "profile.back": "Back",
    "profile.memberSince": "Member since {date}",
    "profile.balance": "Balance",
    "profile.buyCoins": "Buy coins",
    "profile.general": "General",
    "profile.displayName": "Display name",
    "profile.email": "Email",
    "profile.emailReadonly": "Email cannot be changed.",
    "profile.language": "Response language",
    "profile.languageHelp": "Models will answer in the language you pick — perfect for non-English speakers.",
    "profile.preferredVoice": "Preferred voice",
    "profile.previewVoice": "Preview",
    "profile.save": "Save changes",
    "profile.updated": "Profile updated",
    "profile.changePw": "Change password",
    "profile.currentPw": "Current password",
    "profile.newPw": "New password (min 6)",
    "profile.confirmPw": "Confirm new password",
    "profile.pwMismatch": "The two passwords don't match.",
    "profile.updatePw": "Update password",
    "profile.pwUpdated": "Password changed",
    "profile.pwShort": "New password must be at least 6 characters.",
    "profile.purchases": "Purchase history",
    "profile.noPurchases": "No purchases yet.",
    "profile.miniPack": "Mini Pack",
    "profile.valuePack": "Value Pack",
    "profile.danger": "Danger zone",
    "profile.deleteBody": "Permanently delete your account, all searches, pins, and purchase history. This cannot be undone.",
    "profile.deleteBtn": "Delete account",
    "profile.deleteConfirmGoogle": "This will permanently delete your account and all data. Click below to confirm.",
    "profile.deleteConfirmPw": "Enter your password to confirm permanent deletion.",
    "profile.deleteFinal": "Permanently delete",
    "profile.cancel": "Cancel",
    "profile.enterPw": "Enter your password to confirm.",
    "profile.accountDeleted": "Account deleted.",
    "profile.voicePreviewFail": "Could not preview voice.",
    "profile.signOut": "Sign out",

    "pricing.back": "Back",
    "pricing.currentBalance": "Current balance",
    "pricing.eyebrow": "coin packs",
    "pricing.title1": "Top up.",
    "pricing.title2": "One coin = one 5-model compare.",
    "pricing.subtitle": "Pay-as-you-go. No subscription, no expiry. Coins are added instantly after payment.",
    "pricing.bestValue": "Best value",
    "pricing.perCoin": "${amount} per coin",
    "pricing.feature1": "{count} coins ({count} multi-model searches)",
    "pricing.feature2": "All 5 frontier models per query",
    "pricing.buy": "Buy {label}",
    "pricing.footer": "Payments powered by Polar.sh · one-time card, no account needed",
    "pricing.viewPast": "View past purchases",
    "pricing.blurbMini": "Just a taste",
    "pricing.blurbPack": "10x the value",
    "pricing.neverExpire": "Never expire",

    "common.close": "Close",

    "admin.section": "Admin — Model config",
    "admin.description": "You control which OpenRouter model powers each of the five panels. Refresh the catalog to see the newest versions.",
    "admin.currentModel": "Current",
    "admin.chooseModel": "Choose model",
    "admin.vision": "vision",
    "admin.contextK": "{k}k ctx",
    "admin.refresh": "Refresh catalog",
    "admin.refreshing": "Refreshing…",
    "admin.apply": "Apply changes",
    "admin.applying": "Applying…",
    "admin.applied": "Model changes applied.",
    "admin.applyError": "Could not apply changes.",
    "admin.catalogError": "Could not load OpenRouter catalog.",
    "admin.fetchedAt": "Catalog fetched {time}",
    "admin.noChanges": "Nothing to apply.",
    "admin.optionsCount": "{count} options",
    "admin.removeSlot": "Remove this slot",
    "admin.cannotRemoveLast": "At least one model must stay active.",
    "admin.slotRemoved": "Slot removed.",
    "admin.removeError": "Could not remove slot.",
    "admin.addSlot": "Add another AI engine",
    "admin.addSlotTitle": "Add a new AI engine",
    "admin.available": "available",
    "admin.provider": "Provider",
    "admin.confirmAdd": "Add slot",
    "admin.addingSlot": "Adding…",
    "admin.slotAdded": "Slot added — the home page will pick it up automatically.",
    "admin.addError": "Could not add slot.",

    "cookie.eyebrow": "cookies",
    "cookie.title": "Your privacy, your call.",
    "cookie.body": "We use strictly-necessary cookies for sign-in and saving your language preference. Nothing is shared with advertisers. See details in our",
    "cookie.privacyLink": "Privacy Policy",
    "cookie.accept": "Accept",
    "cookie.decline": "Decline",

    "privacy.eyebrow": "privacy policy",
    "privacy.title": "Privacy Policy",
    "privacy.updated": "Last updated: 28 July 2026",
    "privacy.s1.title": "Overview",
    "privacy.s1.body": "wangram.com is a pay-as-you-go comparison tool for frontier AI models. This policy explains what data we collect when you sign up, run a query, or buy coins — and how you can exercise your rights under the GDPR.",
    "privacy.s2.title": "Data we collect",
    "privacy.s2.li1.k": "Account data",
    "privacy.s2.li1.v": "Email, hashed password (never the plaintext), optional display name, chosen voice, chosen response language, coin balance, and account creation time.",
    "privacy.s2.li2.k": "Search data",
    "privacy.s2.li2.v": "The text of every query you submit, any image you attach, the responses from each model, latency and success/failure per model, and which model (if any) you pin as the best answer.",
    "privacy.s2.li3.k": "Payment data",
    "privacy.s2.li3.v": "We do NOT store your card number. Polar.sh processes payments and returns only the session id, amount, and status, which we log for accounting.",
    "privacy.s2.li4.k": "Technical data",
    "privacy.s2.li4.v": "IP address of your API calls (in server logs, typically for 14 days), user-agent string, and standard access logs required to run the service.",
    "privacy.s3.title": "Cookies and local storage",
    "privacy.s3.body": "We use only strictly-necessary storage — no advertising or analytics cookies. When you click Accept or Decline on the banner, we save your choice in a single localStorage entry so we don't ask again.",
    "privacy.s3.li1": "HTTP-only cookies containing your signed session. Deleted when you sign out.",
    "privacy.s3.li2": "Local browser storage for your JWT fallback, preferred language, and cookie-banner decision. Cleared on logout or if you clear site data.",
    "privacy.s4.title": "Third parties who process your data",
    "privacy.s4.body": "We share the minimum needed with the following processors to deliver the service you asked for:",
    "privacy.s4.li1": "Your query text and any attached image are sent to OpenRouter, which forwards them to the five model providers (OpenAI, Anthropic, Google, xAI, DeepSeek).",
    "privacy.s4.li2": "Payment processing. Polar.sh receives your card details directly; we only see the session ID and status.",
    "privacy.s4.li3": "If you use voice input we send the audio blob to OpenAI Whisper; if you play the answer aloud we send the text to OpenAI TTS.",
    "privacy.s4.li4": "If you sign in with Google we receive your email, name, and profile picture from Google. We do not receive your Google password.",
    "privacy.s5.title": "Legal basis (GDPR Art. 6)",
    "privacy.s5.body": "We rely on (a) performance of a contract — we can't run a search without sending your query to the model providers; (b) legitimate interest — server logs and fraud prevention; and (c) your consent — anything beyond strictly-necessary storage, which is currently nothing.",
    "privacy.s6.title": "Retention",
    "privacy.s6.body": "Account and search data are retained until you delete your account (Profile → Danger zone → Delete account). Server access logs are typically rotated after 14 days. Polar.sh payment records are retained for 7 years to comply with tax law.",
    "privacy.s7.title": "Your rights",
    "privacy.s7.body": "You can access, correct, or delete your data at any time from your Profile page. To exercise the right to portability or to object to processing, email us at the address below and we'll respond within 30 days.",
    "privacy.s8.title": "Contact",
    "privacy.s8.body": "Data controller: PrismQuery. Email us at privacy@prismquery.dev for any request, complaint, or question about this policy. You also have the right to lodge a complaint with your local data protection authority.",

    "terms.link": "Terms of Service",
    "terms.eyebrow": "terms of service",
    "terms.title": "Terms of Service",
    "terms.updated": "Last updated: 28 July 2026",
    "terms.s1.title": "1. Acceptance",
    "terms.s1.body": "By creating an account or using wangram.com, you agree to these Terms of Service. If you don't agree, please don't use the service. These terms form a binding contract between you and wangram.com (\"we\", \"us\").",
    "terms.s2.title": "2. What wangram.com does",
    "terms.s2.body": "wangram.com lets you send a single query to multiple frontier AI models (currently OpenAI, Anthropic, Perplexity, xAI, DeepSeek — the exact roster can change) via OpenRouter, and compare their answers side-by-side. Each comparison consumes coins that you either receive free on signup or purchase in packs. There is no subscription.",
    "terms.s3.title": "3. Your account",
    "terms.s3.li1": "You must be at least 16 years old (or the age of digital consent in your country, whichever is higher) to create an account.",
    "terms.s3.li2": "You are responsible for keeping your password confidential and for every action taken on your account.",
    "terms.s3.li3": "Provide accurate information. Do not create accounts under someone else's identity or attempt to impersonate another person.",
    "terms.s4.title": "4. Coins, payments, and refunds",
    "terms.s4.li1": "Coins are a prepaid balance. Each 5-model comparison consumes one coin.",
    "terms.s4.li2": "Payments are processed by Polar.sh. Prices are shown before checkout. All amounts are in USD unless stated otherwise.",
    "terms.s4.li3": "Coins never expire. Purchased coins are non-refundable except when required by law (e.g. right of withdrawal for EU consumers within 14 days on unused balance).",
    "terms.s4.li4": "If a search fails on the majority of models we auto-refund the coin. This is a courtesy and not a guarantee of quality.",
    "terms.s5.title": "5. Acceptable use",
    "terms.s5.intro": "You agree NOT to use wangram.com to:",
    "terms.s5.li1": "Break any law, generate child sexual abuse material, or infringe someone else's intellectual property.",
    "terms.s5.li2": "Generate malware, phishing content, spam, or content designed to harass, defame, or endanger anyone.",
    "terms.s5.li3": "Bypass rate limits, scrape the service, or resell wangram.com access without written permission.",
    "terms.s5.li4": "Upload personal data of third parties without a lawful basis, or upload confidential business data you're not authorised to share.",
    "terms.s6.title": "6. AI-generated output",
    "terms.s6.body": "Model outputs are generated by third-party AI systems and can be inaccurate, biased, or offensive. We do not endorse or verify anything a model returns. You are responsible for how you use, share, or rely on the output — do not use it for medical, legal, financial, or safety-critical decisions without independent review.",
    "terms.s7.title": "7. Your content and IP",
    "terms.s7.body": "You retain ownership of everything you send us (queries, images, pinned favourites). You grant us a limited licence to process that content solely to run your search and store your history. To the extent AI outputs are copyrightable, ownership follows the terms of the underlying model provider — most treat outputs as belonging to the user, but you should verify for high-stakes use.",
    "terms.s8.title": "8. Disclaimer",
    "terms.s8.body": "The service is provided \"as is\". We disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the service will be uninterrupted, error-free, or that a specific model will remain available.",
    "terms.s9.title": "9. Limitation of liability",
    "terms.s9.body": "To the maximum extent permitted by law, our aggregate liability for any claim arising out of the service is limited to the amount you paid us in the 12 months preceding the claim, or USD 100, whichever is greater. We are not liable for indirect, incidental, or consequential damages. Nothing in these terms limits liability that cannot be excluded by law (e.g. for gross negligence or fraud).",
    "terms.s10.title": "10. Suspension and termination",
    "terms.s10.body": "We may suspend or terminate your account if you breach these terms, if we're required to by law, or if your usage puts the service at risk. You can delete your account at any time from Profile → Danger zone → Delete account. Coin balance is forfeited on voluntary deletion.",
    "terms.s11.title": "11. Changes to the service or these terms",
    "terms.s11.body": "We may update these terms occasionally. Material changes will be announced in-app or by email at least 14 days before they take effect. Continued use after the effective date means you accept the new terms. We may also add, change, or remove AI models at any time.",
    "terms.s12.title": "12. Governing law",
    "terms.s12.body": "These terms are governed by the laws of the jurisdiction in which wangram.com is established. Where local consumer-protection law grants you additional rights, those rights are not overridden by this clause.",
    "terms.s13.title": "13. Contact",
    "terms.s13.body": "Questions about these terms? Email legal@prismquery.dev. For privacy questions see our",
  },

  zh: {
    "brand.tagline": "5 个模型并排对比",
    "nav.coins": "金币",
    "nav.buyCoins": "购买更多金币",
    "nav.profile": "个人资料与设置",
    "nav.about": "关于 wangram.com",
    "nav.buy": "购买金币",
    "nav.signOut": "退出登录",
    "nav.language": "语言",
    "nav.newSession": "开始新会话",

    "hero.eyebrow": "无需订阅 · 按需付费",
    "hero.title1": "顶级 AI 模型,",
    "hero.title2": "无需每月订阅费。",
    "hero.subtitle": "提出问题,同时获得 OpenAI、Anthropic、Google、xAI 和 DeepSeek 的答案 —— 并排对比。每次查询消耗 1 枚金币。$0.99 起购 2 枚,永不过期,无需订阅。",
    "hero.footnote": "只在使用时付费。",

    "search.askAnything": "有什么问题?…",
    "search.askAnother": "换一个问题…",
    "search.askAboutImage": "关于这张图片,你想了解什么?",
    "search.compare": "开始对比",
    "search.running": "正在运行",
    "search.dropHere": "将图片拖到此处",
    "search.outOfCoins": "金币已用完 —— 请购买后继续",
    "search.outOfCoinsShort": "金币不足",
    "search.attachHint": "支持拖拽、粘贴或上传图片 · 支持语音输入",
    "search.newInline": "新建",
    "search.yourQuery": "你的问题",
    "search.clearInput": "清空输入",

    "balance.low": "余额不足",
    "balance.out": "金币已用完",
    "balance.left": "你还剩 {credits} 枚金币。每次对比消耗 1 枚。",

    "history.session": "会话",
    "history.title": "历史",
    "history.new": "新建",
    "history.empty": "暂无查询",
    "history.delete": "删除",

    "panel.awaiting": "等待提问",
    "panel.tracing": "追踪中",
    "panel.copy": "复制",
    "panel.copied": "已复制",
    "panel.error": "错误",
    "panel.unknownError": "未知错误",
    "panel.copyAria": "复制回答",

    "leaderboard.title": "你的胜率",
    "leaderboard.pins": "{count} 次置顶",

    "toast.compared": "对比了 {ok}/{total} 个模型",
    "toast.refunded": "只有 {ok}/{total} 个模型回答成功 —— 已退还 1 枚金币。",
    "toast.insufficient": "金币不足。",
    "toast.buyCoins": "购买金币",
    "toast.searchFailed": "搜索失败,请重试。",
    "toast.historyLoadFail": "无法加载历史记录",
    "toast.deleted": "已删除",
    "toast.deleteFail": "删除失败",
    "toast.saveFirst": "请先保存搜索 —— 稍后刷新。",
    "toast.pinned": "已置顶为最佳答案。",
    "toast.pinFail": "无法置顶。",

    "login.eyebrow": "登录",
    "login.title": "进入终端",
    "login.google": "使用 Google 继续",
    "login.or": "或",
    "login.email": "邮箱",
    "login.password": "密码",
    "login.submit": "进入",
    "login.noAccount": "还没有账户?",
    "login.createOne": "立即注册",
    "login.shellNote": "无订阅 —— 仅在使用时付费。",
    "login.registeredOnly": "仅注册用户可访问",
    "login.forgot": "忘记密码?",

    "forgot.eyebrow": "重置密码",
    "forgot.title": "重置密码",
    "forgot.body": "输入你的注册邮箱,我们会发送一封含重置链接的邮件。链接有效期为 1 小时。",
    "forgot.email": "邮箱",
    "forgot.submit": "发送重置链接",
    "forgot.remembered": "想起来了?返回登录",
    "forgot.error": "出错了,请重试。",
    "forgot.checkInbox": "请查收邮件",
    "forgot.sentBody": "如果 {email} 对应有账户,我们已发送重置链接。请同时检查垃圾邮件文件夹。链接 1 小时内有效。",
    "forgot.backToLogin": "返回登录",

    "reset.eyebrow": "新密码",
    "reset.title": "设置新密码",
    "reset.body": "至少 6 个字符。之前的会话将会退出。",
    "reset.newPw": "新密码",
    "reset.confirmPw": "确认新密码",
    "reset.submit": "更新密码",
    "reset.mismatch": "两次输入的密码不一致。",
    "reset.success": "密码已更新",
    "reset.redirecting": "正在跳转到登录页面…",
    "reset.error": "无法重置密码,链接可能已过期。",
    "reset.missingToken": "链接缺少令牌。请在下方申请新链接。",
    "reset.requestNew": "重新申请链接",

    "sessions.eyebrow": "会话",
    "sessions.title": "会话记录",
    "sessions.empty": "暂无会话",
    "sessions.rename": "重命名",
    "sessions.delete": "删除会话",
    "sessions.renamed": "已重命名",
    "sessions.renameFail": "无法重命名。",
    "sessions.deleteConfirm": "确定删除此会话及其所有搜索?此操作不可撤销。",
    "sessions.deleted": "会话已删除",
    "sessions.deleteFail": "无法删除会话。",
    "sessions.saveRename": "保存",
    "sessions.cancelRename": "取消",
    "sessions.moreActions": "更多操作",
    "sessions.expand": "展开侧栏",
    "sessions.collapse": "折叠侧栏",
    "sessions.turnCount": "本次会话共 {count} 轮对话",

    "auth.modelsHeader": "你将获得以下 5 个前沿模型的对比答案",
    "auth.modelsFooter": "实时更新 · 每次 5 模型对比消耗 1 枚金币",

    "register.eyebrow": "注册",
    "register.title": "创建账户",
    "register.google": "使用 Google 注册",
    "register.bonusStrong": "赠送 2 枚金币",
    "register.bonusRest": ",注册即送 —— 相当于 2 次五模型对比。",
    "register.name": "显示名称",
    "register.optional": "可选",
    "register.email": "邮箱",
    "register.password": "密码(至少 6 位)",
    "register.confirmPassword": "确认密码",
    "register.pwMismatch": "两次输入的密码不一致。",
    "register.submit": "创建账户",
    "register.already": "已经注册了?",
    "register.signIn": "登录",
    "register.pwShort": "密码至少需要 6 个字符。",
    "register.agreePrefix": "创建账户即表示我同意",
    "register.agreeAnd": "和",
    "register.mustAgree": "请先同意服务条款和隐私政策。",
    "register.needBotCheck": "请先完成机器人验证。",

    "profile.eyebrow": "个人资料",
    "profile.back": "返回",
    "profile.memberSince": "注册于 {date}",
    "profile.balance": "余额",
    "profile.buyCoins": "购买金币",
    "profile.general": "常规",
    "profile.displayName": "显示名称",
    "profile.email": "邮箱",
    "profile.emailReadonly": "邮箱不可修改。",
    "profile.language": "回复语言",
    "profile.languageHelp": "所有模型将使用你选择的语言回答 —— 非英语用户友好。",
    "profile.preferredVoice": "首选语音",
    "profile.previewVoice": "试听",
    "profile.save": "保存修改",
    "profile.updated": "资料已更新",
    "profile.changePw": "修改密码",
    "profile.currentPw": "当前密码",
    "profile.newPw": "新密码(至少 6 位)",
    "profile.confirmPw": "确认新密码",
    "profile.pwMismatch": "两次输入的密码不一致。",
    "profile.updatePw": "更新密码",
    "profile.pwUpdated": "密码已修改",
    "profile.pwShort": "新密码至少需要 6 个字符。",
    "profile.purchases": "购买记录",
    "profile.noPurchases": "暂无购买记录。",
    "profile.miniPack": "小礼包",
    "profile.valuePack": "超值包",
    "profile.danger": "危险区",
    "profile.deleteBody": "永久删除账户、所有搜索、置顶记录和购买历史。此操作不可撤销。",
    "profile.deleteBtn": "删除账户",
    "profile.deleteConfirmGoogle": "这将永久删除你的账户及所有数据。点击下方确认。",
    "profile.deleteConfirmPw": "输入密码以确认永久删除。",
    "profile.deleteFinal": "永久删除",
    "profile.cancel": "取消",
    "profile.enterPw": "请输入密码以确认。",
    "profile.accountDeleted": "账户已删除。",
    "profile.voicePreviewFail": "无法试听语音。",
    "profile.signOut": "退出登录",

    "pricing.back": "返回",
    "pricing.currentBalance": "当前余额",
    "pricing.eyebrow": "金币包",
    "pricing.title1": "充值。",
    "pricing.title2": "1 枚金币 = 1 次五模型对比。",
    "pricing.subtitle": "按需付费。无订阅、无过期。付款后金币立即到账。",
    "pricing.bestValue": "超值推荐",
    "pricing.perCoin": "${amount} / 枚",
    "pricing.feature1": "{count} 枚金币({count} 次多模型搜索)",
    "pricing.feature2": "每次查询使用全部 5 个前沿模型",
    "pricing.buy": "购买 {label}",
    "pricing.footer": "支付由 Polar.sh 提供 · 一次性付款,无需开户",
    "pricing.viewPast": "查看往期购买",
    "pricing.blurbMini": "浅尝一下",
    "pricing.blurbPack": "10 倍性价比",
    "pricing.neverExpire": "永不过期",

    "common.close": "关闭",

    "cookie.eyebrow": "Cookie",
    "cookie.title": "你的隐私,你做主。",
    "cookie.body": "我们仅使用必要的 Cookie 来完成登录和保存你的语言偏好,不与广告商共享任何信息。详情请见我们的",
    "cookie.privacyLink": "隐私政策",
    "cookie.accept": "接受",
    "cookie.decline": "拒绝",

    "privacy.eyebrow": "隐私政策",
    "privacy.title": "隐私政策",
    "privacy.updated": "最后更新:2026 年 7 月 28 日",
    "privacy.s1.title": "概述",
    "privacy.s1.body": "wangram.com 是一款按需付费的前沿 AI 模型对比工具。本政策说明当你注册、发起查询或购买金币时我们收集哪些数据,以及你在 GDPR 下如何行使权利。",
    "privacy.s2.title": "我们收集的数据",
    "privacy.s2.li1.k": "账户数据",
    "privacy.s2.li1.v": "邮箱、经过哈希处理的密码(绝不存储明文)、可选昵称、选定的语音、回复语言、金币余额和账户创建时间。",
    "privacy.s2.li2.k": "查询数据",
    "privacy.s2.li2.v": "你提交的每条查询文本、附加的图片、各模型的回复、延迟和成败情况,以及你置顶为最佳答案的模型。",
    "privacy.s2.li3.k": "支付数据",
    "privacy.s2.li3.v": "我们不存储你的卡号。Polar.sh 处理支付,只返回会话 ID、金额和状态,用于账务记录。",
    "privacy.s2.li4.k": "技术数据",
    "privacy.s2.li4.v": "API 调用的 IP 地址(服务器日志通常保留 14 天)、用户代理字符串和运行服务所需的标准访问日志。",
    "privacy.s3.title": "Cookie 与本地存储",
    "privacy.s3.body": "我们仅使用必要的存储 —— 没有广告或分析 Cookie。当你点击接受或拒绝时,我们会在 localStorage 中保存一条记录以避免再次询问。",
    "privacy.s3.li1": "包含已签名会话的 HTTP-only Cookie。退出登录时删除。",
    "privacy.s3.li2": "本地浏览器存储,用于 JWT 备用令牌、语言偏好和 Cookie 横幅决定。退出登录或清除站点数据时清空。",
    "privacy.s4.title": "处理你数据的第三方",
    "privacy.s4.body": "为提供你请求的服务,我们仅与以下处理方共享最少必要数据:",
    "privacy.s4.li1": "你的查询文本和附加图片会发送到 OpenRouter,再由其转发到 5 个模型提供商(OpenAI、Anthropic、Google、xAI、DeepSeek)。",
    "privacy.s4.li2": "支付处理。Polar.sh 直接接收你的卡片信息;我们只看到会话 ID 和状态。",
    "privacy.s4.li3": "如你使用语音输入,音频会发送到 OpenAI Whisper;如你朗读答案,文本会发送到 OpenAI TTS。",
    "privacy.s4.li4": "如你使用 Google 登录,我们从 Google 接收邮箱、姓名和头像。我们不会收到你的 Google 密码。",
    "privacy.s5.title": "法律依据(GDPR 第 6 条)",
    "privacy.s5.body": "我们依据:(a) 合同履行 —— 未将查询发送给模型提供商则无法运行搜索;(b) 正当利益 —— 服务器日志和防欺诈;(c) 你的同意 —— 任何超出必要存储的内容(目前为空)。",
    "privacy.s6.title": "保留期",
    "privacy.s6.body": "账户和查询数据将保留至你删除账户(个人资料 → 危险区 → 删除账户)。服务器访问日志通常在 14 天后轮换。Polar.sh 支付记录为遵守税法保留 7 年。",
    "privacy.s7.title": "你的权利",
    "privacy.s7.body": "你可以随时在个人资料页访问、更正或删除你的数据。如需行使数据可携权或反对处理权,请通过下方邮箱联系我们,我们将在 30 天内回复。",
    "privacy.s8.title": "联系方式",
    "privacy.s8.body": "数据控制方:PrismQuery。任何请求、投诉或政策问题请邮件至 privacy@prismquery.dev。你也有权向当地数据保护机构投诉。",

    "terms.link": "服务条款",
    "terms.eyebrow": "服务条款",
    "terms.title": "服务条款",
  },

  hi: {
    "brand.tagline": "5-मॉडल स्प्लिट व्यू",
    "nav.coins": "सिक्के",
    "nav.buyCoins": "और सिक्के खरीदें",
    "nav.profile": "प्रोफ़ाइल और सेटिंग्स",
    "nav.about": "wangram.com के बारे में",
    "nav.buy": "सिक्के खरीदें",
    "nav.signOut": "साइन आउट",
    "nav.language": "भाषा",
    "nav.newSession": "नया सत्र शुरू करें",

    "hero.eyebrow": "कोई सब्सक्रिप्शन नहीं · जितना उपयोग, उतना भुगतान",
    "hero.title1": "प्रो AI मॉडल,",
    "hero.title2": "बिना मासिक शुल्क के।",
    "hero.subtitle": "एक सवाल पूछें और OpenAI, Anthropic, Google, xAI और DeepSeek से एक साथ जवाब पाएँ। हर सवाल पर 1 सिक्का। $0.99 में 2 सिक्के, कभी समाप्त नहीं होते, कोई सब्सक्रिप्शन नहीं।",
    "hero.footnote": "आप तभी भुगतान करते हैं जब आप इसका उपयोग करते हैं।",

    "search.askAnything": "कुछ भी पूछें…",
    "search.askAnother": "एक और सवाल पूछें…",
    "search.askAboutImage": "इस छवि के बारे में क्या जानना चाहते हैं?",
    "search.compare": "तुलना करें",
    "search.running": "चल रहा है",
    "search.dropHere": "यहाँ छवियाँ छोड़ें",
    "search.outOfCoins": "सिक्के समाप्त — जारी रखने के लिए पैक खरीदें",
    "search.outOfCoinsShort": "सिक्के समाप्त",
    "search.attachHint": "छवि छोड़ें, पेस्ट करें या अटैच करें · वॉइस इनपुट समर्थित",
    "search.newInline": "नया",
    "search.yourQuery": "आपका सवाल",
    "search.clearInput": "इनपुट साफ़ करें",

    "balance.low": "कम बैलेंस",
    "balance.out": "सिक्के समाप्त",
    "balance.left": "आपके पास {credits} सिक्के बचे हैं। हर तुलना पर 1 सिक्का।",

    "history.session": "सत्र",
    "history.title": "इतिहास",
    "history.new": "नया",
    "history.empty": "अभी तक कोई सवाल नहीं",
    "history.delete": "हटाएँ",

    "panel.awaiting": "सवाल का इंतज़ार",
    "panel.tracing": "ट्रेस कर रहे हैं",
    "panel.copy": "कॉपी",
    "panel.copied": "कॉपी हो गया",
    "panel.error": "त्रुटि",
    "panel.unknownError": "अज्ञात त्रुटि",
    "panel.copyAria": "उत्तर कॉपी करें",

    "leaderboard.title": "आपकी जीत दर",
    "leaderboard.pins": "{count} पिन",

    "toast.compared": "{ok}/{total} मॉडलों की तुलना की",
    "toast.refunded": "केवल {ok}/{total} मॉडलों ने उत्तर दिया — 1 सिक्का वापस।",
    "toast.insufficient": "सिक्के अपर्याप्त।",
    "toast.buyCoins": "सिक्के खरीदें",
    "toast.searchFailed": "खोज असफल। कृपया पुनः प्रयास करें।",
    "toast.historyLoadFail": "इतिहास लोड नहीं हो सका",
    "toast.deleted": "हटाया गया",
    "toast.deleteFail": "हटाना असफल",
    "toast.saveFirst": "पहले खोज सहेजें — क्षण भर में रीफ़्रेश करें।",
    "toast.pinned": "सर्वोत्तम उत्तर के रूप में पिन किया गया।",
    "toast.pinFail": "पिन नहीं कर सके।",

    "login.eyebrow": "लॉगिन",
    "login.title": "टर्मिनल पर पहुँचें",
    "login.google": "Google से जारी रखें",
    "login.or": "या",
    "login.email": "ईमेल",
    "login.password": "पासवर्ड",
    "login.submit": "प्रवेश",
    "login.noAccount": "खाता नहीं है?",
    "login.createOne": "बनाएँ",
    "login.shellNote": "कोई सब्सक्रिप्शन नहीं — केवल उपयोग पर भुगतान।",
    "login.registeredOnly": "केवल पंजीकृत उपयोगकर्ता",
    "login.forgot": "पासवर्ड भूल गए?",

    "forgot.eyebrow": "पासवर्ड रीसेट",
    "forgot.title": "पासवर्ड रीसेट",
    "forgot.body": "अपना पंजीकृत ईमेल दर्ज करें। हम आपको रीसेट लिंक भेजेंगे। लिंक 1 घंटे के लिए मान्य है।",
    "forgot.email": "ईमेल",
    "forgot.submit": "रीसेट लिंक भेजें",
    "forgot.remembered": "याद आ गया? साइन इन करें",
    "forgot.error": "कुछ गड़बड़ हो गई। कृपया पुनः प्रयास करें।",
    "forgot.checkInbox": "अपना इनबॉक्स देखें",
    "forgot.sentBody": "यदि {email} के लिए खाता मौजूद है, तो हमने रीसेट लिंक भेज दिया है। स्पैम फ़ोल्डर भी देखें। यह 1 घंटे तक मान्य है।",
    "forgot.backToLogin": "साइन इन पर वापस",

    "reset.eyebrow": "नया पासवर्ड",
    "reset.title": "नया पासवर्ड चुनें",
    "reset.body": "कम से कम 6 अक्षर। पुराने सत्रों से साइन आउट किए जाएंगे।",
    "reset.newPw": "नया पासवर्ड",
    "reset.confirmPw": "नया पासवर्ड पुष्टि करें",
    "reset.submit": "पासवर्ड अपडेट करें",
    "reset.mismatch": "दोनों पासवर्ड मेल नहीं खाते।",
    "reset.success": "पासवर्ड अपडेट हुआ",
    "reset.redirecting": "साइन इन पर भेजा जा रहा है…",
    "reset.error": "पासवर्ड रीसेट नहीं हो सका। लिंक समाप्त हो चुका होगा।",
    "reset.missingToken": "इस लिंक में टोकन नहीं है। नीचे नया अनुरोध करें।",
    "reset.requestNew": "नया लिंक अनुरोध करें",

    "sessions.eyebrow": "बातचीतें",
    "sessions.title": "सत्र",
    "sessions.empty": "अभी कोई सत्र नहीं",
    "sessions.rename": "नाम बदलें",
    "sessions.delete": "सत्र हटाएँ",
    "sessions.renamed": "नाम बदल गया",
    "sessions.renameFail": "नाम नहीं बदल सके।",
    "sessions.deleteConfirm": "यह सत्र और इसके सारे प्रश्न हटाना है? यह पूर्ववत नहीं हो सकता।",
    "sessions.deleted": "सत्र हटाया गया",
    "sessions.deleteFail": "सत्र नहीं हटा सके।",
    "sessions.saveRename": "सहेजें",
    "sessions.cancelRename": "रद्द करें",
    "sessions.moreActions": "और विकल्प",
    "sessions.expand": "साइडबार खोलें",
    "sessions.collapse": "साइडबार बंद करें",
    "sessions.turnCount": "इस सत्र में {count} बातचीत",

    "auth.modelsHeader": "आप इन 5 फ्रंटियर मॉडलों के उत्तरों की तुलना करेंगे",
    "auth.modelsFooter": "लाइव अपडेट · हर 5-मॉडल तुलना पर 1 सिक्का",

    "register.eyebrow": "रजिस्टर",
    "register.title": "पहुँच बनाएँ",
    "register.google": "Google से साइन अप",
    "register.bonusStrong": "2 मुफ़्त सिक्के",
    "register.bonusRest": " साइनअप पर — 2 पाँच-मॉडल तुलनाओं के बराबर।",
    "register.name": "प्रदर्शन नाम",
    "register.optional": "वैकल्पिक",
    "register.email": "ईमेल",
    "register.password": "पासवर्ड (कम से कम 6)",
    "register.confirmPassword": "पासवर्ड पुष्टि करें",
    "register.pwMismatch": "दोनों पासवर्ड मेल नहीं खाते।",
    "register.submit": "खाता बनाएँ",
    "register.already": "पहले से पंजीकृत हैं?",
    "register.signIn": "साइन इन",
    "register.pwShort": "पासवर्ड कम से कम 6 अक्षरों का होना चाहिए।",
    "register.agreePrefix": "खाता बनाकर, मैं सहमत हूँ",
    "register.agreeAnd": "और",
    "register.mustAgree": "जारी रखने के लिए कृपया सेवा की शर्तें और गोपनीयता नीति स्वीकार करें।",
    "register.needBotCheck": "कृपया जारी रखने के लिए बॉट जाँच पूरी करें।",

    "profile.eyebrow": "प्रोफ़ाइल",
    "profile.back": "वापस",
    "profile.memberSince": "सदस्य: {date} से",
    "profile.balance": "बैलेंस",
    "profile.buyCoins": "सिक्के खरीदें",
    "profile.general": "सामान्य",
    "profile.displayName": "प्रदर्शन नाम",
    "profile.email": "ईमेल",
    "profile.emailReadonly": "ईमेल बदला नहीं जा सकता।",
    "profile.language": "उत्तर की भाषा",
    "profile.languageHelp": "मॉडल आपकी चुनी गई भाषा में जवाब देंगे।",
    "profile.preferredVoice": "पसंदीदा आवाज़",
    "profile.previewVoice": "सुनें",
    "profile.save": "बदलाव सहेजें",
    "profile.updated": "प्रोफ़ाइल अपडेट हुई",
    "profile.changePw": "पासवर्ड बदलें",
    "profile.currentPw": "वर्तमान पासवर्ड",
    "profile.newPw": "नया पासवर्ड (कम से कम 6)",
    "profile.confirmPw": "नया पासवर्ड पुष्टि करें",
    "profile.pwMismatch": "दोनों पासवर्ड मेल नहीं खाते।",
    "profile.updatePw": "पासवर्ड अपडेट करें",
    "profile.pwUpdated": "पासवर्ड बदल गया",
    "profile.pwShort": "नया पासवर्ड कम से कम 6 अक्षरों का होना चाहिए।",
    "profile.purchases": "खरीद इतिहास",
    "profile.noPurchases": "अभी तक कोई खरीद नहीं।",
    "profile.miniPack": "मिनी पैक",
    "profile.valuePack": "वैल्यू पैक",
    "profile.danger": "खतरा क्षेत्र",
    "profile.deleteBody": "अपना खाता, सभी खोजें, पिन और खरीद इतिहास स्थायी रूप से हटाएँ। यह पूर्ववत नहीं किया जा सकता।",
    "profile.deleteBtn": "खाता हटाएँ",
    "profile.deleteConfirmGoogle": "यह आपके खाते और सारे डेटा को स्थायी रूप से हटा देगा। नीचे क्लिक करके पुष्टि करें।",
    "profile.deleteConfirmPw": "स्थायी हटाने की पुष्टि के लिए पासवर्ड दर्ज करें।",
    "profile.deleteFinal": "स्थायी रूप से हटाएँ",
    "profile.cancel": "रद्द करें",
    "profile.enterPw": "पुष्टि के लिए अपना पासवर्ड डालें।",
    "profile.accountDeleted": "खाता हटा दिया गया।",
    "profile.voicePreviewFail": "आवाज़ नहीं चला सके।",
    "profile.signOut": "साइन आउट",

    "pricing.back": "वापस",
    "pricing.currentBalance": "वर्तमान बैलेंस",
    "pricing.eyebrow": "सिक्का पैक",
    "pricing.title1": "टॉप-अप करें।",
    "pricing.title2": "1 सिक्का = 1 पाँच-मॉडल तुलना।",
    "pricing.subtitle": "जितना उपयोग, उतना भुगतान। कोई सब्सक्रिप्शन नहीं, कोई समाप्ति नहीं। भुगतान के तुरंत बाद सिक्के जुड़ते हैं।",
    "pricing.bestValue": "सर्वोत्तम मूल्य",
    "pricing.perCoin": "${amount} प्रति सिक्का",
    "pricing.feature1": "{count} सिक्के ({count} मल्टी-मॉडल खोजें)",
    "pricing.feature2": "हर सवाल पर सभी 5 फ्रंटियर मॉडल",
    "pricing.buy": "{label} खरीदें",
    "pricing.footer": "भुगतान Polar.sh द्वारा · एक-समय कार्ड, खाता आवश्यक नहीं",
    "pricing.viewPast": "पिछली खरीदारी देखें",
    "pricing.blurbMini": "बस एक झलक",
    "pricing.blurbPack": "10 गुना अधिक मूल्य",
    "pricing.neverExpire": "कभी समाप्त नहीं",

    "common.close": "बंद करें",

    "cookie.eyebrow": "कुकीज़",
    "cookie.title": "आपकी गोपनीयता, आपकी पसंद।",
    "cookie.body": "हम केवल साइन-इन और भाषा वरीयता के लिए ज़रूरी कुकीज़ का उपयोग करते हैं। कुछ भी विज्ञापनदाताओं के साथ साझा नहीं होता। विवरण देखें हमारी",
    "cookie.privacyLink": "गोपनीयता नीति",
    "cookie.accept": "स्वीकार करें",
    "cookie.decline": "अस्वीकार करें",

    "privacy.eyebrow": "गोपनीयता नीति",
    "privacy.title": "गोपनीयता नीति",
    "privacy.updated": "अंतिम अद्यतन: 28 जुलाई 2026",
    "privacy.s1.title": "संक्षेप में",
    "privacy.s1.body": "wangram.com फ्रंटियर AI मॉडलों के लिए पे-एज़-यू-गो तुलना टूल है। यह नीति बताती है कि जब आप साइन अप करते हैं, क्वेरी चलाते हैं या सिक्के खरीदते हैं तो हम कौन-सा डेटा एकत्र करते हैं — और आप GDPR के अंतर्गत अपने अधिकारों का उपयोग कैसे कर सकते हैं।",
    "privacy.s2.title": "हम जो डेटा एकत्र करते हैं",
    "privacy.s2.li1.k": "खाता डेटा",
    "privacy.s2.li1.v": "ईमेल, हैशेड पासवर्ड (कभी सादा पाठ नहीं), वैकल्पिक प्रदर्शन नाम, चुनी हुई आवाज़, उत्तर की भाषा, सिक्के का बैलेंस और खाता बनाने का समय।",
    "privacy.s2.li2.k": "खोज डेटा",
    "privacy.s2.li2.v": "आपकी हर क्वेरी का पाठ, संलग्न कोई भी छवि, हर मॉडल के उत्तर, प्रत्येक मॉडल की लेटेंसी व सफलता/विफलता, और आपने कौन-सा मॉडल सर्वोत्तम के रूप में पिन किया।",
    "privacy.s2.li3.k": "भुगतान डेटा",
    "privacy.s2.li3.v": "हम आपका कार्ड नंबर संग्रहीत नहीं करते। Polar.sh भुगतान संसाधित करता है और केवल सत्र ID, राशि और स्थिति देता है, जिसे हम अकाउंटिंग के लिए लॉग करते हैं।",
    "privacy.s2.li4.k": "तकनीकी डेटा",
    "privacy.s2.li4.v": "आपके API कॉल का IP पता (सर्वर लॉग में, आमतौर पर 14 दिन), यूज़र-एजेंट स्ट्रिंग और सेवा चलाने के लिए आवश्यक मानक एक्सेस लॉग।",
    "privacy.s3.title": "कुकीज़ और लोकल स्टोरेज",
    "privacy.s3.body": "हम केवल आवश्यक स्टोरेज का उपयोग करते हैं — कोई विज्ञापन या एनालिटिक्स कुकीज़ नहीं। जब आप बैनर पर स्वीकार या अस्वीकार क्लिक करते हैं, तो हम आपकी पसंद को एक localStorage एंट्री में सहेज लेते हैं ताकि दोबारा न पूछें।",
    "privacy.s3.li1": "आपके साइन-इन सत्र वाली HTTP-only कुकीज़। साइन आउट पर हटा दी जाती हैं।",
    "privacy.s3.li2": "आपके JWT फ़ॉलबैक, पसंदीदा भाषा और कुकी बैनर निर्णय के लिए स्थानीय ब्राउज़र स्टोरेज। लॉगआउट या साइट डेटा साफ़ करने पर मिटा दी जाती है।",
    "privacy.s4.title": "आपका डेटा प्रोसेस करने वाले तृतीय पक्ष",
    "privacy.s4.body": "आपकी अपेक्षित सेवा प्रदान करने के लिए हम केवल ज़रूरी न्यूनतम डेटा निम्नलिखित प्रोसेसरों के साथ साझा करते हैं:",
    "privacy.s4.li1": "आपकी क्वेरी टेक्स्ट और छवियाँ OpenRouter को भेजी जाती हैं, जो उन्हें 5 मॉडल प्रदाताओं (OpenAI, Anthropic, Google, xAI, DeepSeek) को अग्रेषित करता है।",
    "privacy.s4.li2": "भुगतान प्रोसेसिंग। Polar.sh सीधे आपके कार्ड विवरण प्राप्त करता है; हम केवल सत्र ID और स्थिति देखते हैं।",
    "privacy.s4.li3": "यदि आप वॉइस इनपुट का उपयोग करते हैं तो ऑडियो OpenAI Whisper को भेजा जाता है; यदि आप उत्तर सुनते हैं तो टेक्स्ट OpenAI TTS को भेजा जाता है।",
    "privacy.s4.li4": "यदि आप Google से साइन इन करते हैं तो हमें Google से आपका ईमेल, नाम और प्रोफ़ाइल चित्र मिलता है। आपका Google पासवर्ड हमें नहीं मिलता।",
    "privacy.s5.title": "कानूनी आधार (GDPR अनुच्छेद 6)",
    "privacy.s5.body": "हम आधार लेते हैं: (a) संविदा निष्पादन — मॉडल प्रदाताओं को आपकी क्वेरी भेजे बिना खोज नहीं चला सकते; (b) वैध हित — सर्वर लॉग और धोखाधड़ी रोकथाम; (c) आपकी सहमति — केवल आवश्यक भंडारण से आगे की चीज़ें, जो वर्तमान में कोई नहीं है।",
    "privacy.s6.title": "प्रतिधारण",
    "privacy.s6.body": "खाता व खोज डेटा तब तक रखा जाता है जब तक आप खाता नहीं हटाते (प्रोफ़ाइल → खतरा क्षेत्र → खाता हटाएँ)। सर्वर एक्सेस लॉग आमतौर पर 14 दिन बाद घुमाए जाते हैं। Polar.sh भुगतान रिकॉर्ड कर कानून के अनुपालन के लिए 7 वर्षों तक रखे जाते हैं।",
    "privacy.s7.title": "आपके अधिकार",
    "privacy.s7.body": "आप अपनी प्रोफ़ाइल पेज से कभी भी अपने डेटा तक पहुँच सकते हैं, उसे सही कर सकते हैं या हटा सकते हैं। पोर्टेबिलिटी अधिकार या प्रोसेसिंग पर आपत्ति के लिए हमें नीचे के ईमेल पर लिखें, हम 30 दिनों के भीतर उत्तर देंगे।",
    "privacy.s8.title": "संपर्क",
    "privacy.s8.body": "डेटा नियंत्रक: PrismQuery। किसी भी अनुरोध, शिकायत या प्रश्न के लिए privacy@prismquery.dev पर लिखें। आपको अपने स्थानीय डेटा संरक्षण प्राधिकरण के पास शिकायत दर्ज करने का भी अधिकार है।",

    "terms.link": "सेवा की शर्तें",
    "terms.eyebrow": "सेवा की शर्तें",
    "terms.title": "सेवा की शर्तें",
  },

  es: {
    "brand.tagline": "vista dividida de 5 modelos",
    "nav.coins": "monedas",
    "nav.buyCoins": "Comprar más monedas",
    "nav.profile": "Perfil y ajustes",
    "nav.about": "Acerca de wangram.com",
    "nav.buy": "Comprar monedas",
    "nav.signOut": "Cerrar sesión",
    "nav.language": "Idioma",
    "nav.newSession": "Iniciar una nueva sesión",

    "hero.eyebrow": "sin suscripción · pago por uso",
    "hero.title1": "Modelos de IA profesionales,",
    "hero.title2": "sin la factura mensual.",
    "hero.subtitle": "Haz una pregunta y obtén respuestas de OpenAI, Anthropic, Google, xAI y DeepSeek — en paralelo. Una moneda por consulta. Compra 2 por $0.99, nada caduca, sin suscripción.",
    "hero.footnote": "Solo pagas cuando lo usas.",

    "search.askAnything": "Pregunta lo que quieras…",
    "search.askAnother": "Haz otra pregunta…",
    "search.askAboutImage": "¿Qué quieres saber sobre esta imagen?",
    "search.compare": "Comparar",
    "search.running": "Ejecutando",
    "search.dropHere": "suelta imágenes aquí",
    "search.outOfCoins": "Sin monedas — compra un paquete para continuar",
    "search.outOfCoinsShort": "Sin monedas",
    "search.attachHint": "arrastra, pega o adjunta una imagen · entrada por voz disponible",
    "search.newInline": "Nueva",
    "search.yourQuery": "tu pregunta",
    "search.clearInput": "Borrar entrada",

    "balance.low": "saldo bajo",
    "balance.out": "sin monedas",
    "balance.left": "Te quedan {credits} moneda{plural}. 1 moneda por comparación.",

    "history.session": "Sesión",
    "history.title": "Historial",
    "history.new": "Nueva",
    "history.empty": "Aún no hay consultas",
    "history.delete": "Eliminar",

    "panel.awaiting": "esperando consulta",
    "panel.tracing": "trazando haz",
    "panel.copy": "Copiar",
    "panel.copied": "Copiado",
    "panel.error": "error",
    "panel.unknownError": "Error desconocido",
    "panel.copyAria": "Copiar respuesta",

    "leaderboard.title": "tu tasa de victorias",
    "leaderboard.pins": "{count} pin{plural}",

    "toast.compared": "Comparados {ok}/{total} modelos",
    "toast.refunded": "Solo respondieron {ok}/{total} modelos — 1 moneda reembolsada.",
    "toast.insufficient": "Monedas insuficientes.",
    "toast.buyCoins": "Comprar monedas",
    "toast.searchFailed": "Búsqueda fallida. Inténtalo de nuevo.",
    "toast.historyLoadFail": "No se pudo cargar el historial",
    "toast.deleted": "Eliminado",
    "toast.deleteFail": "Error al eliminar",
    "toast.saveFirst": "Guarda la búsqueda primero — refresca en un momento.",
    "toast.pinned": "Fijada como mejor respuesta.",
    "toast.pinFail": "No se pudo fijar.",

    "login.eyebrow": "inicio de sesión",
    "login.title": "Terminal de acceso",
    "login.google": "Continuar con Google",
    "login.or": "o",
    "login.email": "Correo",
    "login.password": "Contraseña",
    "login.submit": "Entrar",
    "login.noAccount": "¿No tienes cuenta?",
    "login.createOne": "Crear una",
    "login.shellNote": "Sin suscripción — pagas solo cuando lo usas.",
    "login.registeredOnly": "Acceso solo para registrados",
    "login.forgot": "¿Olvidaste tu contraseña?",

    "forgot.eyebrow": "restablecer contraseña",
    "forgot.title": "Restablecer contraseña",
    "forgot.body": "Introduce el correo con el que te registraste. Te enviaremos un enlace para restablecer tu contraseña. Válido por 1 hora.",
    "forgot.email": "Correo",
    "forgot.submit": "Enviar enlace",
    "forgot.remembered": "¿Te acordaste? Inicia sesión",
    "forgot.error": "Algo salió mal. Inténtalo de nuevo.",
    "forgot.checkInbox": "Revisa tu correo",
    "forgot.sentBody": "Si existe una cuenta para {email}, te enviamos un enlace. Revisa también la carpeta de spam. Válido por 1 hora.",
    "forgot.backToLogin": "Volver a iniciar sesión",

    "reset.eyebrow": "nueva contraseña",
    "reset.title": "Elige una nueva contraseña",
    "reset.body": "Al menos 6 caracteres. Se cerrarán las sesiones antiguas.",
    "reset.newPw": "Nueva contraseña",
    "reset.confirmPw": "Confirma la nueva contraseña",
    "reset.submit": "Actualizar contraseña",
    "reset.mismatch": "Las dos contraseñas no coinciden.",
    "reset.success": "Contraseña actualizada",
    "reset.redirecting": "Redirigiéndote al inicio de sesión…",
    "reset.error": "No se pudo restablecer la contraseña. El enlace puede haber caducado.",
    "reset.missingToken": "A este enlace le falta el token. Solicita uno nuevo abajo.",
    "reset.requestNew": "Solicitar un nuevo enlace",

    "sessions.eyebrow": "conversaciones",
    "sessions.title": "Sesiones",
    "sessions.empty": "Aún no hay sesiones",
    "sessions.rename": "Renombrar",
    "sessions.delete": "Eliminar sesión",
    "sessions.renamed": "Sesión renombrada",
    "sessions.renameFail": "No se pudo renombrar.",
    "sessions.deleteConfirm": "¿Eliminar esta sesión y todas sus búsquedas? No se puede deshacer.",
    "sessions.deleted": "Sesión eliminada",
    "sessions.deleteFail": "No se pudo eliminar la sesión.",
    "sessions.saveRename": "Guardar",
    "sessions.cancelRename": "Cancelar",
    "sessions.moreActions": "Más acciones",
    "sessions.expand": "Expandir barra lateral",
    "sessions.collapse": "Contraer barra lateral",
    "sessions.turnCount": "{count} turno{plural} en esta sesión",

    "auth.modelsHeader": "Compararás respuestas de estos 5 modelos frontier",
    "auth.modelsFooter": "Actualizado en vivo · una moneda por comparación de 5 modelos",

    "register.eyebrow": "registro",
    "register.title": "Crear acceso",
    "register.google": "Registrarse con Google",
    "register.bonusStrong": "2 monedas gratis",
    "register.bonusRest": " al registrarte — equivalen a 2 comparaciones de 5 modelos.",
    "register.name": "Nombre para mostrar",
    "register.optional": "opcional",
    "register.email": "Correo",
    "register.password": "Contraseña (mín. 6)",
    "register.confirmPassword": "Confirmar contraseña",
    "register.pwMismatch": "Las dos contraseñas no coinciden.",
    "register.submit": "Crear cuenta",
    "register.already": "¿Ya te registraste?",
    "register.signIn": "Iniciar sesión",
    "register.pwShort": "La contraseña debe tener al menos 6 caracteres.",
    "register.agreePrefix": "Al crear una cuenta, acepto los",
    "register.agreeAnd": "y la",
    "register.mustAgree": "Debes aceptar los Términos y la Política de Privacidad para continuar.",
    "register.needBotCheck": "Por favor, completa la verificación anti-bots para continuar.",

    "profile.eyebrow": "perfil",
    "profile.back": "Volver",
    "profile.memberSince": "Miembro desde {date}",
    "profile.balance": "Saldo",
    "profile.buyCoins": "Comprar monedas",
    "profile.general": "General",
    "profile.displayName": "Nombre para mostrar",
    "profile.email": "Correo",
    "profile.emailReadonly": "El correo no se puede cambiar.",
    "profile.language": "Idioma de respuesta",
    "profile.languageHelp": "Los modelos responderán en el idioma que elijas.",
    "profile.preferredVoice": "Voz preferida",
    "profile.previewVoice": "Escuchar",
    "profile.save": "Guardar cambios",
    "profile.updated": "Perfil actualizado",
    "profile.changePw": "Cambiar contraseña",
    "profile.currentPw": "Contraseña actual",
    "profile.newPw": "Nueva contraseña (mín. 6)",
    "profile.confirmPw": "Confirmar nueva contraseña",
    "profile.pwMismatch": "Las dos contraseñas no coinciden.",
    "profile.updatePw": "Actualizar contraseña",
    "profile.pwUpdated": "Contraseña cambiada",
    "profile.pwShort": "La nueva contraseña debe tener al menos 6 caracteres.",
    "profile.purchases": "Historial de compras",
    "profile.noPurchases": "Aún no hay compras.",
    "profile.miniPack": "Pack mini",
    "profile.valuePack": "Pack de valor",
    "profile.danger": "Zona peligrosa",
    "profile.deleteBody": "Elimina permanentemente tu cuenta, todas las búsquedas, pines e historial de compras. Esto no se puede deshacer.",
    "profile.deleteBtn": "Eliminar cuenta",
    "profile.deleteConfirmGoogle": "Esto eliminará permanentemente tu cuenta y todos los datos. Haz clic abajo para confirmar.",
    "profile.deleteConfirmPw": "Ingresa tu contraseña para confirmar la eliminación permanente.",
    "profile.deleteFinal": "Eliminar permanentemente",
    "profile.cancel": "Cancelar",
    "profile.enterPw": "Ingresa tu contraseña para confirmar.",
    "profile.accountDeleted": "Cuenta eliminada.",
    "profile.voicePreviewFail": "No se pudo previsualizar la voz.",
    "profile.signOut": "Cerrar sesión",

    "pricing.back": "Volver",
    "pricing.currentBalance": "Saldo actual",
    "pricing.eyebrow": "packs de monedas",
    "pricing.title1": "Recarga.",
    "pricing.title2": "1 moneda = 1 comparación de 5 modelos.",
    "pricing.subtitle": "Pago por uso. Sin suscripción, sin caducidad. Las monedas se añaden al instante después del pago.",
    "pricing.bestValue": "Mejor valor",
    "pricing.perCoin": "${amount} por moneda",
    "pricing.feature1": "{count} monedas ({count} búsquedas multi-modelo)",
    "pricing.feature2": "Los 5 modelos frontier por cada consulta",
    "pricing.buy": "Comprar {label}",
    "pricing.footer": "Pagos con Polar.sh · pago único, sin cuenta",
    "pricing.viewPast": "Ver compras pasadas",
    "pricing.blurbMini": "Solo una prueba",
    "pricing.blurbPack": "10 veces más valor",
    "pricing.neverExpire": "Nunca caducan",

    "common.close": "Cerrar",

    "cookie.eyebrow": "cookies",
    "cookie.title": "Tu privacidad, tu decisión.",
    "cookie.body": "Usamos solo cookies estrictamente necesarias para iniciar sesión y guardar tu idioma. No se comparte nada con anunciantes. Detalles en nuestra",
    "cookie.privacyLink": "Política de privacidad",
    "cookie.accept": "Aceptar",
    "cookie.decline": "Rechazar",

    "privacy.eyebrow": "política de privacidad",
    "privacy.title": "Política de privacidad",
    "privacy.updated": "Última actualización: 28 de julio de 2026",
    "privacy.s1.title": "Resumen",
    "privacy.s1.body": "wangram.com es una herramienta de comparación de modelos de IA frontier con pago por uso. Esta política explica qué datos recopilamos cuando te registras, ejecutas una consulta o compras monedas — y cómo ejercer tus derechos bajo el RGPD.",
    "privacy.s2.title": "Datos que recopilamos",
    "privacy.s2.li1.k": "Datos de cuenta",
    "privacy.s2.li1.v": "Correo, contraseña con hash (nunca en texto plano), nombre opcional, voz seleccionada, idioma de respuesta, saldo de monedas y fecha de creación.",
    "privacy.s2.li2.k": "Datos de búsqueda",
    "privacy.s2.li2.v": "El texto de cada consulta, cualquier imagen adjunta, las respuestas de cada modelo, latencia y éxito/fallo, y qué modelo fijaste como el mejor.",
    "privacy.s2.li3.k": "Datos de pago",
    "privacy.s2.li3.v": "NO almacenamos tu número de tarjeta. Polar.sh procesa los pagos y solo nos devuelve el ID de sesión, el monto y el estado, que registramos para contabilidad.",
    "privacy.s2.li4.k": "Datos técnicos",
    "privacy.s2.li4.v": "Dirección IP de tus llamadas a la API (en logs del servidor, normalmente 14 días), user-agent y logs estándar necesarios para operar el servicio.",
    "privacy.s3.title": "Cookies y almacenamiento local",
    "privacy.s3.body": "Usamos solo almacenamiento estrictamente necesario — sin cookies de publicidad ni analítica. Al pulsar Aceptar o Rechazar guardamos tu decisión en una única entrada de localStorage para no volver a preguntar.",
    "privacy.s3.li1": "Cookies HTTP-only con tu sesión firmada. Se eliminan al cerrar sesión.",
    "privacy.s3.li2": "Almacenamiento local del navegador para tu JWT de respaldo, idioma y decisión del banner. Se borra al cerrar sesión o limpiar datos del sitio.",
    "privacy.s4.title": "Terceros que procesan tus datos",
    "privacy.s4.body": "Compartimos lo mínimo necesario con los siguientes procesadores para prestar el servicio que solicitas:",
    "privacy.s4.li1": "Tu consulta e imágenes adjuntas se envían a OpenRouter, que los reenvía a los 5 proveedores de modelos (OpenAI, Anthropic, Google, xAI, DeepSeek).",
    "privacy.s4.li2": "Procesamiento de pagos. Polar.sh recibe directamente tu tarjeta; nosotros solo vemos el ID de sesión y el estado.",
    "privacy.s4.li3": "Si usas la entrada por voz enviamos el audio a OpenAI Whisper; si escuchas la respuesta enviamos el texto a OpenAI TTS.",
    "privacy.s4.li4": "Si te registras con Google, recibimos tu correo, nombre y foto de perfil desde Google. No recibimos tu contraseña de Google.",
    "privacy.s5.title": "Base legal (RGPD art. 6)",
    "privacy.s5.body": "Nos basamos en: (a) ejecución del contrato — no podemos realizar una búsqueda sin enviar tu consulta a los proveedores; (b) interés legítimo — logs y prevención de fraude; (c) tu consentimiento — cualquier cosa más allá del almacenamiento estrictamente necesario, que actualmente es nada.",
    "privacy.s6.title": "Retención",
    "privacy.s6.body": "Los datos de cuenta y búsqueda se conservan hasta que elimines tu cuenta (Perfil → Zona peligrosa → Eliminar cuenta). Los logs del servidor se rotan normalmente a los 14 días. Los registros de pago de Polar.sh se conservan 7 años por obligación fiscal.",
    "privacy.s7.title": "Tus derechos",
    "privacy.s7.body": "Puedes acceder, corregir o eliminar tus datos desde tu página de perfil en cualquier momento. Para ejercer la portabilidad u oponerte al procesamiento, escríbenos al correo de abajo y responderemos en 30 días.",
    "privacy.s8.title": "Contacto",
    "privacy.s8.body": "Responsable del tratamiento: PrismQuery. Escríbenos a privacy@prismquery.dev para cualquier solicitud, queja o consulta. También tienes derecho a presentar una reclamación ante tu autoridad de protección de datos local.",

    "terms.link": "Términos de servicio",
    "terms.eyebrow": "términos de servicio",
    "terms.title": "Términos de servicio",
  },

  ar: {
    "brand.tagline": "عرض مقسم لخمسة نماذج",
    "nav.coins": "عملات",
    "nav.buyCoins": "شراء المزيد من العملات",
    "nav.profile": "الملف الشخصي والإعدادات",
    "nav.about": "حول wangram.com",
    "nav.buy": "شراء عملات",
    "nav.signOut": "تسجيل الخروج",
    "nav.language": "اللغة",
    "nav.newSession": "بدء جلسة جديدة",

    "hero.eyebrow": "بدون اشتراك · ادفع بحسب الاستخدام",
    "hero.title1": "نماذج ذكاء اصطناعي احترافية،",
    "hero.title2": "بدون فاتورة شهرية.",
    "hero.subtitle": "اطرح سؤالاً واحصل على إجابات من OpenAI و Anthropic و Google و xAI و DeepSeek — جنبًا إلى جنب. عملة واحدة لكل استعلام. اشتر 2 مقابل 0.99$، لا تنتهي صلاحيتها، بدون اشتراك.",
    "hero.footnote": "تدفع فقط عند الاستخدام الفعلي.",

    "search.askAnything": "اسأل عن أي شيء…",
    "search.askAnother": "اطرح سؤالاً آخر…",
    "search.askAboutImage": "ماذا تريد أن تعرف عن هذه الصورة؟",
    "search.compare": "قارن",
    "search.running": "جارٍ التنفيذ",
    "search.dropHere": "أفلت الصور هنا",
    "search.outOfCoins": "نفدت العملات — اشتر باقة للمتابعة",
    "search.outOfCoinsShort": "نفدت العملات",
    "search.attachHint": "اسحب، الصق، أو أرفق صورة · الإدخال الصوتي مدعوم",
    "search.newInline": "جديد",
    "search.yourQuery": "سؤالك",
    "search.clearInput": "مسح الإدخال",

    "balance.low": "رصيد منخفض",
    "balance.out": "نفدت العملات",
    "balance.left": "لديك {credits} عملة متبقية. عملة واحدة لكل مقارنة.",

    "history.session": "الجلسة",
    "history.title": "السجل",
    "history.new": "جديد",
    "history.empty": "لا توجد استعلامات بعد",
    "history.delete": "حذف",

    "panel.awaiting": "بانتظار الاستعلام",
    "panel.tracing": "جارٍ التتبع",
    "panel.copy": "نسخ",
    "panel.copied": "تم النسخ",
    "panel.error": "خطأ",
    "panel.unknownError": "خطأ غير معروف",
    "panel.copyAria": "نسخ الإجابة",

    "leaderboard.title": "معدل فوزك",
    "leaderboard.pins": "{count} تثبيت",

    "toast.compared": "تمت مقارنة {ok}/{total} نماذج",
    "toast.refunded": "أجاب فقط {ok}/{total} نماذج — تم استرداد عملة واحدة.",
    "toast.insufficient": "العملات غير كافية.",
    "toast.buyCoins": "شراء عملات",
    "toast.searchFailed": "فشل البحث. حاول مرة أخرى.",
    "toast.historyLoadFail": "تعذّر تحميل سجل الاستعلام",
    "toast.deleted": "تم الحذف",
    "toast.deleteFail": "فشل الحذف",
    "toast.saveFirst": "احفظ البحث أولاً — أعد التحميل بعد لحظة.",
    "toast.pinned": "تم التثبيت كأفضل إجابة.",
    "toast.pinFail": "تعذّر التثبيت.",

    "login.eyebrow": "تسجيل الدخول",
    "login.title": "بوابة الوصول",
    "login.google": "المتابعة باستخدام Google",
    "login.or": "أو",
    "login.email": "البريد الإلكتروني",
    "login.password": "كلمة المرور",
    "login.submit": "دخول",
    "login.noAccount": "لا يوجد حساب؟",
    "login.createOne": "أنشئ حسابًا",
    "login.shellNote": "بدون اشتراك — تدفع فقط عند الاستخدام.",
    "login.registeredOnly": "الوصول للمسجلين فقط",
    "login.forgot": "هل نسيت كلمة المرور؟",

    "forgot.eyebrow": "إعادة تعيين كلمة المرور",
    "forgot.title": "إعادة تعيين كلمة المرور",
    "forgot.body": "أدخل البريد الإلكتروني الذي سجّلت به. سنرسل لك رابط إعادة التعيين. يبقى صالحًا لمدة ساعة واحدة.",
    "forgot.email": "البريد الإلكتروني",
    "forgot.submit": "إرسال الرابط",
    "forgot.remembered": "تذكرتها؟ سجّل الدخول",
    "forgot.error": "حدث خطأ. يرجى المحاولة مرة أخرى.",
    "forgot.checkInbox": "تحقق من بريدك",
    "forgot.sentBody": "إذا كان لدى {email} حساب، فقد أرسلنا رابط إعادة التعيين. تحقق أيضًا من مجلد الرسائل غير المرغوبة. الرابط صالح لمدة ساعة.",
    "forgot.backToLogin": "العودة إلى تسجيل الدخول",

    "reset.eyebrow": "كلمة مرور جديدة",
    "reset.title": "اختر كلمة مرور جديدة",
    "reset.body": "6 أحرف على الأقل. سيتم تسجيل الخروج من الجلسات القديمة.",
    "reset.newPw": "كلمة المرور الجديدة",
    "reset.confirmPw": "تأكيد كلمة المرور الجديدة",
    "reset.submit": "تحديث كلمة المرور",
    "reset.mismatch": "كلمتا المرور غير متطابقتين.",
    "reset.success": "تم تحديث كلمة المرور",
    "reset.redirecting": "جاري توجيهك إلى تسجيل الدخول…",
    "reset.error": "تعذّر إعادة تعيين كلمة المرور. قد يكون الرابط منتهي الصلاحية.",
    "reset.missingToken": "هذا الرابط ينقصه الرمز. اطلب رابطًا جديدًا أدناه.",
    "reset.requestNew": "طلب رابط جديد",

    "sessions.eyebrow": "المحادثات",
    "sessions.title": "الجلسات",
    "sessions.empty": "لا توجد جلسات بعد",
    "sessions.rename": "إعادة تسمية",
    "sessions.delete": "حذف الجلسة",
    "sessions.renamed": "تمت إعادة التسمية",
    "sessions.renameFail": "تعذّرت إعادة التسمية.",
    "sessions.deleteConfirm": "هل تريد حذف هذه الجلسة وكل بحوثها؟ لا يمكن التراجع.",
    "sessions.deleted": "تم حذف الجلسة",
    "sessions.deleteFail": "تعذّر حذف الجلسة.",
    "sessions.saveRename": "حفظ",
    "sessions.cancelRename": "إلغاء",
    "sessions.moreActions": "المزيد",
    "sessions.expand": "توسيع الشريط الجانبي",
    "sessions.collapse": "طي الشريط الجانبي",
    "sessions.turnCount": "{count} جولات في هذه الجلسة",

    "auth.modelsHeader": "ستقارن إجابات هذه النماذج الخمسة المتقدمة",
    "auth.modelsFooter": "تحديث مباشر · عملة واحدة لكل مقارنة من 5 نماذج",

    "register.eyebrow": "التسجيل",
    "register.title": "إنشاء حساب",
    "register.google": "التسجيل باستخدام Google",
    "register.bonusStrong": "عملتان مجانيتان",
    "register.bonusRest": " عند التسجيل — تعادل مقارنتين لخمسة نماذج.",
    "register.name": "اسم العرض",
    "register.optional": "اختياري",
    "register.email": "البريد الإلكتروني",
    "register.password": "كلمة المرور (6 أحرف على الأقل)",
    "register.confirmPassword": "تأكيد كلمة المرور",
    "register.pwMismatch": "كلمتا المرور غير متطابقتين.",
    "register.submit": "إنشاء الحساب",
    "register.already": "مسجّل مسبقًا؟",
    "register.signIn": "تسجيل الدخول",
    "register.pwShort": "يجب أن تكون كلمة المرور 6 أحرف على الأقل.",
    "register.agreePrefix": "بإنشاء حساب، أوافق على",
    "register.agreeAnd": "و",
    "register.mustAgree": "يرجى قبول شروط الخدمة وسياسة الخصوصية للمتابعة.",
    "register.needBotCheck": "يرجى إكمال فحص الروبوت للمتابعة.",

    "profile.eyebrow": "الملف الشخصي",
    "profile.back": "رجوع",
    "profile.memberSince": "عضو منذ {date}",
    "profile.balance": "الرصيد",
    "profile.buyCoins": "شراء عملات",
    "profile.general": "عام",
    "profile.displayName": "اسم العرض",
    "profile.email": "البريد الإلكتروني",
    "profile.emailReadonly": "لا يمكن تغيير البريد.",
    "profile.language": "لغة الإجابة",
    "profile.languageHelp": "ستجيب النماذج باللغة التي تختارها.",
    "profile.preferredVoice": "الصوت المفضل",
    "profile.previewVoice": "استمع",
    "profile.save": "حفظ التغييرات",
    "profile.updated": "تم تحديث الملف",
    "profile.changePw": "تغيير كلمة المرور",
    "profile.currentPw": "كلمة المرور الحالية",
    "profile.newPw": "كلمة المرور الجديدة (6 على الأقل)",
    "profile.confirmPw": "تأكيد كلمة المرور الجديدة",
    "profile.pwMismatch": "كلمتا المرور غير متطابقتين.",
    "profile.updatePw": "تحديث كلمة المرور",
    "profile.pwUpdated": "تم تغيير كلمة المرور",
    "profile.pwShort": "يجب أن تكون كلمة المرور الجديدة 6 أحرف على الأقل.",
    "profile.purchases": "سجل المشتريات",
    "profile.noPurchases": "لا توجد مشتريات بعد.",
    "profile.miniPack": "الباقة الصغيرة",
    "profile.valuePack": "الباقة الأكبر",
    "profile.danger": "منطقة الخطر",
    "profile.deleteBody": "حذف الحساب وجميع البحوث والتثبيتات وسجل المشتريات نهائيًا. لا يمكن التراجع.",
    "profile.deleteBtn": "حذف الحساب",
    "profile.deleteConfirmGoogle": "سيؤدي هذا إلى حذف حسابك وكل بياناته بشكل دائم. اضغط أدناه للتأكيد.",
    "profile.deleteConfirmPw": "أدخل كلمة المرور لتأكيد الحذف الدائم.",
    "profile.deleteFinal": "حذف دائم",
    "profile.cancel": "إلغاء",
    "profile.enterPw": "أدخل كلمة المرور للتأكيد.",
    "profile.accountDeleted": "تم حذف الحساب.",
    "profile.voicePreviewFail": "تعذّر تشغيل الصوت.",
    "profile.signOut": "تسجيل الخروج",

    "pricing.back": "رجوع",
    "pricing.currentBalance": "الرصيد الحالي",
    "pricing.eyebrow": "باقات العملات",
    "pricing.title1": "أعد الشحن.",
    "pricing.title2": "عملة واحدة = مقارنة خمسة نماذج.",
    "pricing.subtitle": "ادفع بحسب الاستخدام. بدون اشتراك، بدون انتهاء صلاحية. تُضاف العملات فورًا بعد الدفع.",
    "pricing.bestValue": "أفضل قيمة",
    "pricing.perCoin": "${amount} لكل عملة",
    "pricing.feature1": "{count} عملة ({count} عملية بحث متعددة النماذج)",
    "pricing.feature2": "جميع النماذج الخمسة المتقدمة لكل استعلام",
    "pricing.buy": "شراء {label}",
    "pricing.footer": "المدفوعات عبر Polar.sh · دفعة واحدة بدون حساب",
    "pricing.viewPast": "عرض المشتريات السابقة",
    "pricing.blurbMini": "مجرد تجربة",
    "pricing.blurbPack": "قيمة أعلى بعشرة أضعاف",
    "pricing.neverExpire": "لا تنتهي أبدًا",

    "common.close": "إغلاق",

    "cookie.eyebrow": "ملفات تعريف الارتباط",
    "cookie.title": "خصوصيتك، قرارك.",
    "cookie.body": "نستخدم فقط ملفات تعريف الارتباط الضرورية لتسجيل الدخول وحفظ لغتك. لا نشارك أي شيء مع المعلنين. راجع التفاصيل في",
    "cookie.privacyLink": "سياسة الخصوصية",
    "cookie.accept": "قبول",
    "cookie.decline": "رفض",

    "privacy.eyebrow": "سياسة الخصوصية",
    "privacy.title": "سياسة الخصوصية",
    "privacy.updated": "آخر تحديث: 28 يوليو 2026",
    "privacy.s1.title": "نظرة عامة",
    "privacy.s1.body": "wangram.com أداة مقارنة لنماذج الذكاء الاصطناعي المتقدمة بنظام الدفع حسب الاستخدام. توضح هذه السياسة البيانات التي نجمعها عند التسجيل أو تشغيل استعلام أو شراء عملات — وكيف تمارس حقوقك بموجب اللائحة العامة لحماية البيانات (GDPR).",
    "privacy.s2.title": "البيانات التي نجمعها",
    "privacy.s2.li1.k": "بيانات الحساب",
    "privacy.s2.li1.v": "البريد الإلكتروني، كلمة المرور المشفرة (لا يتم تخزين النص الصريح)، الاسم الاختياري، الصوت المختار، لغة الرد، رصيد العملات، ووقت إنشاء الحساب.",
    "privacy.s2.li2.k": "بيانات البحث",
    "privacy.s2.li2.v": "نص كل استعلام، أي صورة تُرفق، الإجابات من كل نموذج، زمن الاستجابة والنجاح/الفشل، والنموذج الذي ثبّته كأفضل إجابة.",
    "privacy.s2.li3.k": "بيانات الدفع",
    "privacy.s2.li3.v": "نحن لا نخزّن رقم بطاقتك. تعالج Polar.sh المدفوعات وترسل لنا فقط معرّف الجلسة والمبلغ والحالة لأغراض المحاسبة.",
    "privacy.s2.li4.k": "بيانات تقنية",
    "privacy.s2.li4.v": "عنوان IP لطلبات الـ API (في سجلات الخادم، عادة لمدة 14 يومًا)، سلسلة User-Agent، وسجلات الوصول القياسية اللازمة لتشغيل الخدمة.",
    "privacy.s3.title": "الكوكيز والتخزين المحلي",
    "privacy.s3.body": "نستخدم فقط التخزين الضروري — لا كوكيز إعلانات ولا تحليلات. عند الضغط على قبول أو رفض نحفظ اختيارك في إدخال localStorage واحد لعدم السؤال مجددًا.",
    "privacy.s3.li1": "كوكيز HTTP-only تحتوي جلستك الموقعة. تُحذف عند تسجيل الخروج.",
    "privacy.s3.li2": "تخزين محلي في المتصفح لـ JWT الاحتياطي، اللغة المفضّلة، وقرار لافتة الكوكيز. يُمسح عند تسجيل الخروج أو مسح بيانات الموقع.",
    "privacy.s4.title": "الأطراف الثالثة التي تعالج بياناتك",
    "privacy.s4.body": "نشارك الحد الأدنى اللازم مع المعالجين التاليين لتقديم الخدمة التي طلبتها:",
    "privacy.s4.li1": "يُرسل نص استعلامك وأي صورة مرفقة إلى OpenRouter، الذي يعيد توجيهها إلى مزوّدي النماذج الخمسة (OpenAI، Anthropic، Google، xAI، DeepSeek).",
    "privacy.s4.li2": "معالجة الدفع. تستقبل Polar.sh تفاصيل بطاقتك مباشرة؛ نحن نرى فقط معرّف الجلسة والحالة.",
    "privacy.s4.li3": "إذا استخدمت الإدخال الصوتي نرسل الصوت إلى OpenAI Whisper؛ إذا استمعت إلى الإجابة نرسل النص إلى OpenAI TTS.",
    "privacy.s4.li4": "إذا سجلت الدخول عبر Google فنستقبل منه بريدك واسمك وصورة ملفك. لا نتلقى كلمة مرورك في Google.",
    "privacy.s5.title": "الأساس القانوني (المادة 6 من GDPR)",
    "privacy.s5.body": "نستند إلى: (أ) تنفيذ العقد — لا يمكن تشغيل بحث دون إرسال استعلامك للمزوّدين؛ (ب) المصلحة المشروعة — سجلات الخادم ومنع الاحتيال؛ (ج) موافقتك — لأي شيء يتجاوز التخزين الضروري، وهو حاليًا لا شيء.",
    "privacy.s6.title": "مدة الاحتفاظ",
    "privacy.s6.body": "تُحفظ بيانات الحساب والبحث حتى تحذف حسابك (الملف الشخصي → منطقة الخطر → حذف الحساب). تُدوَّر سجلات الخادم عادة بعد 14 يومًا. تُحفظ سجلات مدفوعات Polar.sh لمدة 7 سنوات امتثالًا لقوانين الضرائب.",
    "privacy.s7.title": "حقوقك",
    "privacy.s7.body": "يمكنك الوصول إلى بياناتك أو تصحيحها أو حذفها من صفحة ملفك الشخصي في أي وقت. لممارسة الحق في نقل البيانات أو الاعتراض على المعالجة، راسلنا عبر البريد أدناه وسنرد خلال 30 يومًا.",
    "privacy.s8.title": "الاتصال",
    "privacy.s8.body": "المسؤول عن البيانات: PrismQuery. راسلنا على privacy@prismquery.dev لأي طلب أو شكوى أو استفسار. لك أيضًا الحق في تقديم شكوى إلى هيئة حماية البيانات المحلية.",

    "terms.link": "شروط الخدمة",
    "terms.eyebrow": "شروط الخدمة",
    "terms.title": "شروط الخدمة",
  },
};

/**
 * Look up a translation. Falls back to English, then to the raw key.
 * Supports `{name}` placeholders in the translated string.
 */
export function translate(lang, key, vars = {}) {
  const source = DICT[lang] || DICT[DEFAULT_LANGUAGE];
  let str = source[key];
  if (str == null) str = DICT[DEFAULT_LANGUAGE][key];
  if (str == null) return key;
  return String(str).replace(/\{(\w+)\}/g, (_, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : ""
  );
}
